'use client';

import { c as _c } from "react/compiler-runtime";
import { type Button, easeOutTransition } from '@repo/ui';
import { AnimatePresence, motion } from 'framer-motion';
import { type ComponentProps, type ReactNode, useEffect, useRef, useState } from 'react';
import { useActionFlow } from '../ActionFlowProvider';
import { ActionFlowExecutionButton } from './ActionFlowExecutionButton';
import { ActionFlowRevertedButton } from './ActionFlowRevertedButton';
import { ActionFlowSteps } from './ActionFlowSteps';
import { ActionFlowSuccessButton } from './ActionFlowSuccessButton';
const transition = easeOutTransition;
interface ActionFlowWidgetProps {
  successContent?: ReactNode;
  revertedContent?: ReactNode;
  leftSlot?: ReactNode;
  disabled?: boolean;
  variant?: ComponentProps<typeof Button>['variant'];
  onClose?: (status: 'success' | 'reverted') => void;
}

// Standalone widget to be used inside the ActionFlowProvider that implements the entire simple flow (button, step states, terminal state)
export function ActionFlowWidget(t0) {
  const $ = _c(28);
  const {
    successContent,
    revertedContent,
    leftSlot,
    disabled,
    variant,
    onClose
  } = t0;
  const {
    chainId,
    state,
    hasSteps
  } = useActionFlow();
  const showLeftSlot = state.flowState === "idle" || state.flowState === "reverted";
  const contentRef = useRef(null);
  const [height, setHeight] = useState("auto");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const content = contentRef.current;
      if (!content) {
        return;
      }
      const observer = new ResizeObserver(() => {
        if (content.offsetHeight) {
          setHeight(content.offsetHeight);
        }
      });
      observer.observe(content);
      return () => observer.disconnect();
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== state.flowState) {
    t2 = [state.flowState];
    $[1] = state.flowState;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (!hasSteps || !chainId) {
    return null;
  }
  let t3;
  if ($[3] !== chainId || $[4] !== disabled || $[5] !== onClose || $[6] !== revertedContent || $[7] !== state.flowState || $[8] !== state.txHash || $[9] !== successContent || $[10] !== variant) {
    t3 = () => {
      switch (state.flowState) {
        case "idle":
          {
            return <ActionFlowExecutionButton disabled={disabled} variant={variant} />;
          }
        case "execution":
          {
            return <ActionFlowSteps />;
          }
        case "success":
          {
            return successContent ?? <ActionFlowSuccessButton hash={state.txHash} chainId={chainId} buttonProps={{
              onClick: () => onClose?.("success")
            }} />;
          }
        case "reverted":
          {
            return revertedContent ?? <ActionFlowRevertedButton hash={state.txHash} chainId={chainId} buttonProps={{
              onClick: () => onClose?.("reverted")
            }} />;
          }
      }
    };
    $[3] = chainId;
    $[4] = disabled;
    $[5] = onClose;
    $[6] = revertedContent;
    $[7] = state.flowState;
    $[8] = state.txHash;
    $[9] = successContent;
    $[10] = variant;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const renderContent = t3;
  let t4;
  if ($[12] !== height) {
    t4 = {
      height
    };
    $[12] = height;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const t5 = state.flowState;
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      opacity: 0,
      y: 4,
      filter: "blur(4px)"
    };
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: {
        ...transition,
        delay: transition.duration / 3
      }
    };
    t8 = {
      opacity: 0,
      y: -4,
      filter: "blur(4px)"
    };
    $[15] = t7;
    $[16] = t8;
  } else {
    t7 = $[15];
    t8 = $[16];
  }
  const t9 = showLeftSlot ? leftSlot : null;
  let t10;
  if ($[17] !== renderContent) {
    t10 = renderContent();
    $[17] = renderContent;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t9) {
    t11 = <div ref={contentRef} className="flex w-full gap-2">{t9}{t10}</div>;
    $[19] = t10;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== state.flowState || $[23] !== t11) {
    t12 = <AnimatePresence mode="popLayout" initial={false}><motion.div key={t5} initial={t6} animate={t7} exit={t8} transition={transition}>{t11}</motion.div></AnimatePresence>;
    $[22] = state.flowState;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12 || $[26] !== t4) {
    t13 = <motion.div animate={t4} transition={transition} className="w-full overflow-hidden">{t12}</motion.div>;
    $[25] = t12;
    $[26] = t4;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  return t13;
}